import {
  Database,
  Check,
  Loader2,
  BookmarkPlus,
  AlertCircle,
} from 'lucide-react';
import { useState } from 'react';
import type { CustomToolRenderer } from '@cotera/client/v0/chat/tool-renderers';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { Button } from '@cotera/client/v0/components/ui/button';
import { cn } from '@cotera/client/v0/lib/utils';

const filenameFromInput = (input: Record<string, unknown>): string => {
  const filename = input['filename'];
  return typeof filename === 'string' && filename !== ''
    ? filename
    : 'your file';
};

const datasetIdFromInput = (input: Record<string, unknown>): string | null => {
  const datasetId = input['datasetId'];
  return typeof datasetId === 'string' && datasetId !== '' ? datasetId : null;
};

/**
 * Renders the `coco/upload-dataset` tool call: the in-chat stand-in for a
 * tabular file dropped into the chat. While the import is running the tool call
 * is incomplete and this shows an "uploading…" row; once complete it becomes a
 * dataset card that opens the dataset and offers "Save as dataset".
 *
 * Whether it has been saved to the library is read from the tool call's own
 * output (`promoted`), which the server updates on promotion — via this button,
 * the panel, or Coco's promote tool — so the card flips consistently over the
 * chat's live stream. A local optimistic flag covers the click's own latency.
 */
export const UploadDatasetToolRenderer: CustomToolRenderer = ({
  part,
  readonly,
}) => {
  const artifactPanel = useArtifactPanel();
  const client = useTenantedClient();
  const [savedOptimistic, setSavedOptimistic] = useState(false);
  const [saving, setSaving] = useState(false);
  const artifact = part.getArtifact();
  const output = part.getOutput<{ promoted?: boolean; failed?: boolean }>();

  // Import failed: the server completes the placeholder with `failed` and no
  // artifact, so this is checked before the still-importing branch (which also
  // sees a null artifact) to avoid spinning forever.
  if (output?.failed === true) {
    return (
      <div className="-mt-3 mb-2 ml-auto flex w-fit max-w-md items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 text-sm text-muted-foreground">
          Couldn&apos;t import{' '}
          <span className="font-medium text-foreground">
            {filenameFromInput(part.input)}
          </span>{' '}
          as a dataset.
        </div>
      </div>
    );
  }

  // Still importing: the tool call has no artifact output yet.
  if (artifact === null) {
    return (
      <div className="-mt-3 mb-2 ml-auto flex w-fit max-w-md items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        <div className="min-w-0 text-sm text-muted-foreground">
          Uploading{' '}
          <span className="font-medium text-foreground">
            {filenameFromInput(part.input)}
          </span>{' '}
          as a dataset…
        </div>
      </div>
    );
  }

  const datasetId =
    artifact.artifactId ?? datasetIdFromInput(part.input) ?? part.toolCallId;
  const promoted = savedOptimistic || output?.promoted === true;

  const save = async (): Promise<void> => {
    setSavedOptimistic(true);
    setSaving(true);
    try {
      const result = await client.resource.dataset.promote({ id: datasetId });
      if (result.isErr()) {
        setSavedOptimistic(false);
      }
    } catch {
      setSavedOptimistic(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-mt-3 mb-2 ml-auto flex w-fit max-w-md items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-left">
      <button
        type="button"
        onClick={
          artifactPanel !== null
            ? () => artifactPanel.focusArtifact(datasetId)
            : undefined
        }
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity hover:opacity-80"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
          <Database className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {filenameFromInput(part.input)}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            Dataset · click to open
          </div>
        </div>
      </button>
      {readonly === true ? null : promoted ? (
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5" />
          Saved to library
        </span>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={saving}
          onClick={() => void save()}
          className={cn('shrink-0 gap-1.5 text-xs')}
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <BookmarkPlus className="h-3.5 w-3.5" />
          )}
          Save as dataset
        </Button>
      )}
    </div>
  );
};
